import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { Banner } from "@faro-lotv/flat-ui";
import {
  RegistrationRevision,
  RegistrationState,
  RevisionType,
} from "@faro-lotv/service-wires";
import { memo, type JSX } from "react";

interface DataPreparationBannersProps {
  /** currently loaded revision */
  revision: RegistrationRevision;
}

/** @returns the banners to show in the data-preparation page */
export const DataPreparationBanners = memo(function DataPreparationBanners({
  revision,
}: DataPreparationBannersProps): JSX.Element {
  return (
    <DataPreparationStatusBanner
      revisionState={revision.state}
      revisionType={revision.revisionType ?? undefined}
    />
  );
});

type DataPreparationStatusBannerProps = {
  /** The current state of the registration. */
  revisionState: RegistrationState;
  /** The type of the revision, in case of special revisions (e.g. Draft). */
  revisionType?: RevisionType;
};

function DataPreparationStatusBanner({
  revisionState,
  revisionType,
}: DataPreparationStatusBannerProps): JSX.Element | null {
  const hasRegistrationDevFeature = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );

  // Draft revisions are special and currently don't have a useful state indicator
  // Just hide the banner in this case
  if (revisionType === RevisionType.draft) {
    return null;
  }

  // Also do not show any banners to the end user
  if (!hasRegistrationDevFeature) {
    return null;
  }

  switch (revisionState) {
    case RegistrationState.merged:
      return (
        <Banner variant="success" title="Revision Merged">
          This revision has been merged to Draft.
        </Banner>
      );
    case RegistrationState.canceled:
      return (
        <Banner variant="error" title="Registration canceled">
          The registration has been canceled. Please restart the workflow.
        </Banner>
      );
    default:
      return null;
  }
}
